import React from 'react';

// Fixed internal coordinate space; the SVG scales to 100% width via viewBox.
const W = 720;
const H = 280;
const PAD = { top: 16, right: 16, bottom: 34, left: 36 };
const PLOT_W = W - PAD.left - PAD.right;
const PLOT_H = H - PAD.top - PAD.bottom;
const RATING_MAX = 10;

const EXPERIENCE_COLOR = '#4299e1';
const TEAM_COLOR = '#48bb78';
const VOLUME_COLOR = '#cbd5e0';

function xForIndex(index, count) {
  if (count <= 1) return PAD.left + PLOT_W / 2;
  return PAD.left + (index / (count - 1)) * PLOT_W;
}

function yForRating(value) {
  return PAD.top + (1 - value / RATING_MAX) * PLOT_H;
}

function shortDate(dateStr) {
  // "2026-07-06" -> "07-06"
  const parts = String(dateStr).split('-');
  return parts.length === 3 ? `${parts[1]}-${parts[2]}` : dateStr;
}

function RatingLine({ trend, valueKey, color }) {
  const points = trend
    .map((entry, index) => ({ entry, index }))
    .filter(({ entry }) => (entry[valueKey] || 0) > 0)
    .map(({ entry, index }) => ({
      x: xForIndex(index, trend.length),
      y: yForRating(entry[valueKey]),
    }));

  if (points.length === 0) return null;

  const polyline = points.map((p) => `${p.x},${p.y}`).join(' ');

  return (
    <g>
      {points.length > 1 && (
        <polyline
          points={polyline}
          fill="none"
          stroke={color}
          strokeWidth="2.5"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      )}
      {points.map((p, i) => (
        <circle key={i} cx={p.x} cy={p.y} r="3.5" fill={color} />
      ))}
    </g>
  );
}

export default function HackathonTrendChart({ trend }) {
  if (!trend || trend.length === 0) {
    return <p style={{ color: '#666', fontSize: '0.85rem' }}>No data yet.</p>;
  }

  const count = trend.length;
  const maxVolume = Math.max(...trend.map((e) => e.count), 1);
  const barWidth = Math.max(4, Math.min(28, (PLOT_W / count) * 0.45));

  const gridValues = [0, 2, 4, 6, 8, 10];
  // Show at most ~8 x-axis labels to avoid crowding.
  const labelStep = Math.max(1, Math.ceil(count / 8));

  return (
    <div className="hackathon-trend">
      <div className="hackathon-trend-legend">
        <span className="hackathon-trend-key">
          <span className="hackathon-trend-swatch" style={{ background: EXPERIENCE_COLOR }} />
          Avg Overall Experience
        </span>
        <span className="hackathon-trend-key">
          <span className="hackathon-trend-swatch" style={{ background: TEAM_COLOR }} />
          Avg Team Rating
        </span>
        <span className="hackathon-trend-key">
          <span className="hackathon-trend-swatch hackathon-trend-swatch-bar" style={{ background: VOLUME_COLOR }} />
          Responses / day
        </span>
      </div>

      <svg
        className="hackathon-trend-svg"
        viewBox={`0 0 ${W} ${H}`}
        preserveAspectRatio="xMidYMid meet"
        role="img"
        aria-label="Hackathon survey trend over time"
      >
        {/* Horizontal gridlines + y-axis labels (0-10 rating scale) */}
        {gridValues.map((v) => {
          const y = yForRating(v);
          return (
            <g key={v}>
              <line
                x1={PAD.left}
                y1={y}
                x2={W - PAD.right}
                y2={y}
                stroke="#edf2f7"
                strokeWidth="1"
              />
              <text x={PAD.left - 6} y={y + 3} textAnchor="end" className="hackathon-trend-axis">
                {v}
              </text>
            </g>
          );
        })}

        {/* Response volume bars (scaled to their own max, faint, behind lines) */}
        {trend.map((entry, index) => {
          const barH = (entry.count / maxVolume) * PLOT_H;
          const x = xForIndex(index, count) - barWidth / 2;
          const y = PAD.top + PLOT_H - barH;
          return (
            <rect
              key={`bar-${entry.date}`}
              x={x}
              y={y}
              width={barWidth}
              height={barH}
              fill={VOLUME_COLOR}
              opacity="0.55"
              rx="2"
            >
              <title>{`${entry.date}: ${entry.count} response(s)`}</title>
            </rect>
          );
        })}

        {/* Rating trend lines */}
        <RatingLine trend={trend} valueKey="avg_overall_experience" color={EXPERIENCE_COLOR} />
        <RatingLine trend={trend} valueKey="avg_team_rating" color={TEAM_COLOR} />

        {/* X-axis date labels */}
        {trend.map((entry, index) => {
          if (index % labelStep !== 0 && index !== count - 1) return null;
          return (
            <text
              key={`x-${entry.date}`}
              x={xForIndex(index, count)}
              y={H - PAD.bottom + 18}
              textAnchor="middle"
              className="hackathon-trend-axis"
            >
              {shortDate(entry.date)}
            </text>
          );
        })}
      </svg>
    </div>
  );
}
